import { Link, useLocation, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/lib/auth-context";

// Admin-only chrome. Deliberately NOT the consumer AppShell — the client
// sidebar (garage, car counts, bottom-nav) doesn't apply to an operator
// console. Reuses the same design-system classes so it stays visually
// consistent without dragging in useUserData.
export function AdminShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const isDashboard = pathname === "/admin";
  const isClients = pathname.startsWith("/admin/clienti");
  const isSuspects = pathname.startsWith("/admin/suspecti");
  const isSettings = pathname.startsWith("/admin/setari");
  const isHelp = pathname.startsWith("/admin/ajutor");

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <div className="app">
      <aside className="side">
        <Link to="/admin" className="side-brand">
          <span className="side-brand-mark">
            <img src="/simple_icon.svg" alt="" width={36} height={24} />
          </span>
          Admin · Asistent Auto
        </Link>

        <div className="side-eyebrow">Panou</div>
        <ul className="side-list">
          <li>
            <Link
              to="/admin"
              className={`side-link${isDashboard ? " is-active" : ""}`}
            >
              <Icon name="dashboard" />
              Dashboard
            </Link>
          </li>
          <li>
            <Link
              to="/admin/clienti"
              className={`side-link${isClients ? " is-active" : ""}`}
            >
              <Icon name="group" />
              Clienți
            </Link>
          </li>
          <li>
            <Link
              to="/admin/suspecti"
              className={`side-link${isSuspects ? " is-active" : ""}`}
            >
              <Icon name="gpp_maybe" />
              Conturi suspecte
            </Link>
          </li>
          <li>
            <Link
              to="/admin/ajutor"
              className={`side-link${isHelp ? " is-active" : ""}`}
            >
              <Icon name="help" />
              Centru de ajutor
            </Link>
          </li>
          <li>
            <Link
              to="/admin/setari"
              className={`side-link${isSettings ? " is-active" : ""}`}
            >
              <Icon name="tune" />
              Setări
            </Link>
          </li>
        </ul>

        <div className="side-foot">
          <div className="side-account">
            <span className="avatar">
              <Icon name="shield_person" />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                className="name"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user?.email ?? "—"}
              </div>
              <div className="role">Administrator</div>
            </div>
            <button
              type="button"
              className="more"
              onClick={handleSignOut}
              title="Deconectează-te"
            >
              <Icon name="logout" />
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <div className="page">{children}</div>
        {/* Below 880px `.side` is display:none, so without this the operator
            console had no navigation at all on a phone (and `.page` was
            already reserving 72px for a nav that wasn't rendered). Same
            five destinations as the sidebar. */}
        <nav className="bottom-nav" aria-label="Navigare administrare">
          <Link to="/admin" className={`bottom-nav-item${isDashboard ? " is-active" : ""}`}>
            <span className="bottom-nav-pill"><Icon name="dashboard" /></span>
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/clienti" className={`bottom-nav-item${isClients ? " is-active" : ""}`}>
            <span className="bottom-nav-pill"><Icon name="group" /></span>
            <span>Clienți</span>
          </Link>
          <Link to="/admin/suspecti" className={`bottom-nav-item${isSuspects ? " is-active" : ""}`}>
            <span className="bottom-nav-pill"><Icon name="gpp_maybe" /></span>
            <span>Suspecte</span>
          </Link>
          <Link to="/admin/ajutor" className={`bottom-nav-item${isHelp ? " is-active" : ""}`}>
            <span className="bottom-nav-pill"><Icon name="help" /></span>
            <span>Ajutor</span>
          </Link>
          <Link to="/admin/setari" className={`bottom-nav-item${isSettings ? " is-active" : ""}`}>
            <span className="bottom-nav-pill"><Icon name="tune" /></span>
            <span>Setări</span>
          </Link>
        </nav>
      </div>
    </div>
  );
}
